/**
 * ValidationLineItemRow - Individual line item row for validation
 */

import { Check, Pencil, X } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useValidationStore } from '../../store';

export interface ValidationLineItemRowProps {
  section: string;
  itemIndex: number;
  lineNumber?: string;
  label?: string;
  amount?: number;
  borrowerPaidAtClosing?: number;
  borrowerPaidBeforeClosing?: number;
  sellerPaidAtClosing?: number;
  sellerPaidBeforeClosing?: number;
  paidByOthers?: number;
}

export function ValidationLineItemRow({
  section,
  itemIndex,
  lineNumber,
  label,
  amount,
  borrowerPaidAtClosing,
  borrowerPaidBeforeClosing,
  sellerPaidAtClosing,
  sellerPaidBeforeClosing,
  paidByOthers,
}: ValidationLineItemRowProps) {
  const { selectedFields, toggleLineItem, setLineItemField } = useValidationStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState('');
  const [editAmount, setEditAmount] = useState('');

  const sectionData = selectedFields.lineItems?.find((s) => s.section === section);
  const item = sectionData?.items[itemIndex];
  const isChecked = item?.include ?? true;
  const currentLabel = (item?.fields?.label?.value as string | undefined) ?? label ?? '';
  const currentAmount = (item?.fields?.amount?.value as number | undefined) ?? amount;
  const wasModified = item?.fields?.label?.wasModified || item?.fields?.amount?.wasModified;

  const handleSave = () => {
    if (editLabel !== label) {
      setLineItemField(section, itemIndex, 'label', editLabel);
    }
    if (editAmount) {
      setLineItemField(section, itemIndex, 'amount', parseFloat(editAmount));
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditLabel(String(currentLabel));
    setEditAmount(String(currentAmount ?? 0));
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const formatCurrency = (val: number | undefined): string => {
    if (val === undefined || val === 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(val);
  };

  // Check if this item has payment breakdowns
  const hasPaymentBreakdown =
    borrowerPaidAtClosing !== undefined ||
    borrowerPaidBeforeClosing !== undefined ||
    sellerPaidAtClosing !== undefined ||
    sellerPaidBeforeClosing !== undefined ||
    paidByOthers !== undefined;

  return (
    <div
      className={`p-3 rounded-lg border transition-colors ${
        isChecked ? 'bg-background' : 'bg-muted/30 opacity-60'
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Checkbox */}
        <Checkbox
          checked={isChecked}
          onCheckedChange={() => toggleLineItem(section, itemIndex)}
          className="shrink-0"
        />

        {/* Line Number */}
        {lineNumber && (
          <div className="w-20 shrink-0">
            <span className="text-xs text-muted-foreground">{lineNumber}</span>
          </div>
        )}

        {/* Label */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <Input
              type="text"
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              onKeyDown={handleKeyPress}
              className="h-8"
              placeholder="Label"
            />
          ) : (
            <span className="text-sm font-medium block truncate">
              {currentLabel || label || 'No label'}
            </span>
          )}
        </div>

        {/* Amount */}
        <div className="w-28 shrink-0 text-right">
          {isEditing ? (
            <Input
              type="number"
              value={editAmount}
              onChange={(e) => setEditAmount(e.target.value)}
              onKeyDown={handleKeyPress}
              className="h-8"
              placeholder="0.00"
            />
          ) : (
            <span className="text-sm font-mono font-semibold">
              {formatCurrency(currentAmount ?? amount)}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {wasModified && !isEditing && (
            <span className="text-xs text-amber-600 dark:text-amber-400">Modified</span>
          )}
          {isEditing ? (
            <>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={handleSave}>
                <Check className="h-4 w-4 text-green-600" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={handleCancel}>
                <X className="h-4 w-4 text-destructive" />
              </Button>
            </>
          ) : (
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => {
                setEditLabel(currentLabel ?? label ?? '');
                setEditAmount(String(currentAmount ?? amount ?? 0));
                setIsEditing(true);
              }}
            >
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Payment Breakdown */}
      {hasPaymentBreakdown && (
        <div className="mt-2 ml-8 grid grid-cols-5 gap-2 text-xs">
          <div className="text-center">
            <div className="text-muted-foreground mb-1">Borrower @ Closing</div>
            <div className="font-mono">{formatCurrency(borrowerPaidAtClosing)}</div>
          </div>
          <div className="text-center">
            <div className="text-muted-foreground mb-1">Borrower Before</div>
            <div className="font-mono">{formatCurrency(borrowerPaidBeforeClosing)}</div>
          </div>
          <div className="text-center">
            <div className="text-muted-foreground mb-1">Seller @ Closing</div>
            <div className="font-mono">{formatCurrency(sellerPaidAtClosing)}</div>
          </div>
          <div className="text-center">
            <div className="text-muted-foreground mb-1">Seller Before</div>
            <div className="font-mono">{formatCurrency(sellerPaidBeforeClosing)}</div>
          </div>
          <div className="text-center">
            <div className="text-muted-foreground mb-1">Paid by Others</div>
            <div className="font-mono">{formatCurrency(paidByOthers)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
